import { UploadOutlined } from '@ant-design/icons'
import { Alert, Button, Form, Input, message } from 'admin-web-ui'
import { ApolloClient } from '@apollo/client'
import { Store } from 'rc-field-form/lib/interface'
import React from 'react'
import { useAsyncFn } from 'react-use'
import styled from 'styled-components'
import { SingleFileUpload } from '../../../../components/Form/SingleFileUpload'
import { readTextFromBlob } from './read-text-from-blob'
import { uploadDeviceCheckKey } from './upload-device-check-key'

const KeyInputsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`

const StyledFormItem = styled(Form.Item)`
  flex-grow: 1;
  &:nth-child(1) {
    margin-right: 8px;
    margin-bottom: 8px;
  }
  &:nth-child(2) {
    margin-left: 8px;
    margin-bottom: 8px;
  }
`

const FooterContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 0 -24px -8px;
  padding: 18px 24px 0;
`

const StyledButton = styled(Button)`
  min-width: 112px;
`

export const SubmitButton = styled(StyledButton)`
  margin-left: 12px;
`

interface Props {
  projectClient: ApolloClient<unknown>
  onSuccess: () => void
  onCancel: () => void
}

interface FormData {
  keyId: string
  issuer: string
  file: File
}

export const UploadDeviceCheckKeyForm: React.FC<Props> = (props) => {
  const { projectClient, onSuccess, onCancel } = props

  const [submitState, handleSubmit] = useAsyncFn(
    async (values?: Store) => {
      if (!values) {
        return undefined
      }

      const formData = values as FormData
      const fileContent = await readTextFromBlob(formData.file)

      await uploadDeviceCheckKey(projectClient, {
        keyId: formData.keyId,
        issuer: formData.issuer,
        privateKey: fileContent,
      })

      message.success('DeviceCheck Key Uploaded!')
      onSuccess()
    },
    [projectClient],
  )

  return (
    <Form onFinish={handleSubmit}>
      <KeyInputsContainer>
        <StyledFormItem
          name="keyId"
          label="Key ID"
          rules={[{ required: true, message: 'Please input a Key ID' }]}
        >
          <Input type="text" placeholder="Enter Key ID" />
        </StyledFormItem>
        <StyledFormItem
          name="issuer"
          label="Team ID"
          rules={[{ required: true, message: 'Please input a Team ID' }]}
        >
          <Input type="text" placeholder="Enter Team ID" />
        </StyledFormItem>
      </KeyInputsContainer>
      <Form.Item
        name="file"
        label="Key File"
        rules={[{ required: true, message: 'Click to choose .key file' }]}
      >
        <SingleFileUpload>
          <Button
            className="automation-KeyFileUploadButton"
            icon={<UploadOutlined />}
            shape="round"
            type="default"
          >
            Click to choose .key file
          </Button>
        </SingleFileUpload>
      </Form.Item>
      {submitState.error && (
        <Alert
          type="error"
          message="There was an error uploading your DeviceCheck key. Please try again."
        />
      )}
      <FooterContainer>
        <StyledButton
          onClick={onCancel}
          className="automation-DeviceCheckKeyCancelButton"
          type="default"
          shape="round"
        >
          Cancel
        </StyledButton>
        <SubmitButton
          className="automation-DeviceCheckKeySubmitButton"
          type="primary"
          htmlType="submit"
          loading={submitState.loading}
          shape="round"
        >
          Upload Key
        </SubmitButton>
      </FooterContainer>
    </Form>
  )
}
